import { css } from "@emotion/css";
import { Badge, Checkbox } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ProjectSettingsMetadata, ExtensionSettingsValues, ProjectResource, GitRefResource, RunbookResource } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, HasRunbooksInGit, Permission, Repository, toGitBranch } from "@octopusdeploy/octopus-server-client";
import { cloneDeep } from "lodash";
import pluralize from "pluralize";
import type * as React from "react";
import { repository } from "~/clientInstance";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import RunbookMultiSelect from "~/components/MultiSelect/RunbookMultiSelect";
import { PageContent } from "~/components/PageContent/PageContent";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import { ExpandableFormSection, FormSectionHeading, Note, Summary } from "~/components/form/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
interface ITSMProvidersSettingsPageProps {
    project: ProjectResource;
    gitRef: GitRefResource | string | undefined;
    onProjectUpdated: (project: ProjectResource, gitRef: GitRefResource | string | undefined) => Promise<void>;
}
interface ITSMProvidersSettingsPageInternalProps extends ITSMProvidersSettingsPageProps {
    octopusApprovalsFeatureEnabled: boolean;
}
interface ITSMProvidersSettingsState extends OptionalFormBaseComponentState<ExtensionSettingsValues[]> {
    project: ProjectResource | null;
    metaData: ProjectSettingsMetadata[];
    runbooks: RunbookResource[];
    isTrial: boolean;
}
interface ServiceNowEnvironmentSettings {
    ExtensionId: "servicenow-integration";
    Values: {
        ServiceNowChangeControlled: boolean;
        ServiceNowConnectionId: string;
        AutomaticStateTransitionTarget: string;
        StandardChangeTemplateName: string;
        ServiceNowRunbookIds: string[];
    };
}
interface JiraServiceManagementSettings {
    ExtensionId: "jiraservicemanagement-integration";
    Values: {
        JsmChangeControlled: boolean;
        JsmConnectionId: string;
        ServiceDeskProjectName: string;
        JsmRunbookIds: string[];
    };
}
interface OctopusApprovalsSettings {
    ExtensionId: "octopus-approvals";
    Values: {
        OctopusApprovalsChangeControlled: boolean;
        OctopusApprovalsRunbookIds: string[];
    };
}
class ITSMProvidersSettingsPageInternal extends OptionalFormBaseComponent<ITSMProvidersSettingsPageInternalProps, ITSMProvidersSettingsState, ExtensionSettingsValues[]> {
    constructor(props: ITSMProvidersSettingsPageInternalProps) {
        super(props);
        this.state = {
            project: null,
            metaData: [],
            runbooks: [],
            isTrial: false,
        };
    }
    private async saveProject(project: ProjectResource) {
        const result = await repository.Projects.save(project);
        await this.props.onProjectUpdated(result, this.props.gitRef);
        this.setState(() => {
            return {
                model: result.ExtensionSettings,
                cleanModel: cloneDeep(result.ExtensionSettings),
                project: result,
            };
        });
    }
    private async loadAllRunbooks(project: ProjectResource) {
        const params = { skip: 0, take: Repository.takeAll };
        if (HasGitPersistenceSettings(project.PersistenceSettings) && HasRunbooksInGit(project.PersistenceSettings)) {
            const defaultBranch = toGitBranch(project.PersistenceSettings.DefaultBranch);
            const runbooks = await repository.Runbooks.getRunbooks(project, defaultBranch, params);
            return runbooks.Items;
        }
        else {
            const runbooks = await repository.Runbooks.getRunbooks(project, undefined, params);
            return runbooks.Items;
        }
    }
    getExtensionSettings(project: ProjectResource): ExtensionSettingsValues[] {
        const defaultItsmProviderSettings = this.defaultSettings();
        if (project?.ExtensionSettings == undefined || project?.ExtensionSettings.length === 0) {
            return defaultItsmProviderSettings;
        }
        return defaultItsmProviderSettings.every((s) => project?.ExtensionSettings.findIndex((d) => d.ExtensionId === s.ExtensionId) !== -1)
            ? project?.ExtensionSettings
            : project?.ExtensionSettings.concat(defaultItsmProviderSettings.filter((d) => project?.ExtensionSettings.findIndex((s) => s.ExtensionId === d.ExtensionId) === -1));
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const project = this.props.project;
            const [metaData, runbooks, licenseStatus] = await Promise.all([repository.Projects.getMetadata(project), this.loadAllRunbooks(project), repository.Licenses.getCurrentStatus()]);
            const extensionSettings = this.getExtensionSettings(project);
            this.setState({
                project,
                metaData,
                model: extensionSettings,
                cleanModel: cloneDeep(extensionSettings),
                runbooks,
                isTrial: licenseStatus.IsTrial,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    defaultSettings = () => {
        const snowDefaultSettings: ServiceNowEnvironmentSettings = {
            ExtensionId: "servicenow-integration",
            Values: {
                ServiceNowChangeControlled: false,
                ServiceNowConnectionId: "",
                AutomaticStateTransitionTarget: "None",
                StandardChangeTemplateName: "",
                ServiceNowRunbookIds: [],
            },
        };
        const jsmDefaultSettings: JiraServiceManagementSettings = {
            ExtensionId: "jiraservicemanagement-integration",
            Values: {
                JsmChangeControlled: false,
                JsmConnectionId: "",
                ServiceDeskProjectName: "",
                JsmRunbookIds: [],
            },
        };
        const octopusApprovalsDefaultSettings: OctopusApprovalsSettings = {
            ExtensionId: "octopus-approvals",
            Values: {
                OctopusApprovalsChangeControlled: false,
                OctopusApprovalsRunbookIds: [],
            },
        };
        const defaultSettings: ExtensionSettingsValues[] = [snowDefaultSettings, jsmDefaultSettings, octopusApprovalsDefaultSettings];
        return defaultSettings;
    };
    render() {
        return (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} savePermission={this.editPermission()} onSaveClick={this.handleSaveClick} saveText="ITSM provider settings updated" forceDisableFormSaveButton={false}>
                {({ FormContent, createSaveAction }) => (<PageContent header={{ title: "ITSM Providers Settings", primaryAction: createSaveAction({}) }} busy={this.state.busy} errors={this.errors}>
                        <FormContent hideExpandAll={false}>
                            {this.state.metaData === undefined ? (<></>) : (<ExtensionSettings metadata={this.state.metaData} extensionSettings={this.state.model ?? []} refreshModel={() => {
                        this.setState({
                            model: this.state.model,
                        });
                    }} runbooks={this.state.runbooks} octopusApprovalsFeatureEnabled={this.props.octopusApprovalsFeatureEnabled} isTrial={this.state.isTrial}/>)}
                        </FormContent>
                    </PageContent>)}
            </LegacyForm>);
    }
    handleSaveClick = async () => {
        const model = this.state.model;
        if (!model || !this.state.project) {
            throw "no model loaded";
        }
        const project: ProjectResource = {
            ...this.state.project,
            ExtensionSettings: cloneDeep(model),
        };
        await this.doBusyTask(async () => {
            await this.saveProject(project);
        });
    };
    private editPermission(): PermissionCheckProps {
        return {
            permission: Permission.ProjectEdit,
            project: this.state.project?.Id,
            tenant: "*",
        };
    }
}
export function ITSMProvidersSettingsPage(props: ITSMProvidersSettingsPageProps) {
    const octopusApprovalsFeatureEnabled = useOctopusFeatureToggle("octopus-approvals", false);
    return <ITSMProvidersSettingsPageInternal {...props} octopusApprovalsFeatureEnabled={octopusApprovalsFeatureEnabled}/>;
}
function isServiceNowEnvironmentSettings(extensionSettings: ExtensionSettingsValues): extensionSettings is ServiceNowEnvironmentSettings {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (extensionSettings as ServiceNowEnvironmentSettings).ExtensionId === "servicenow-integration";
}
function isJiraServiceManagementSettings(extensionSettings: ExtensionSettingsValues): extensionSettings is JiraServiceManagementSettings {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (extensionSettings as JiraServiceManagementSettings).ExtensionId === "jiraservicemanagement-integration";
}
function isOctopusApprovalsSettings(extensionSettings: ExtensionSettingsValues): extensionSettings is OctopusApprovalsSettings {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (extensionSettings as OctopusApprovalsSettings).ExtensionId === "octopus-approvals";
}
const ExtensionSettings: React.FC<{
    metadata: ProjectSettingsMetadata[];
    extensionSettings: ExtensionSettingsValues[];
    refreshModel: () => void;
    runbooks: RunbookResource[];
    octopusApprovalsFeatureEnabled: boolean;
    isTrial: boolean;
}> = (props: {
    metadata: ProjectSettingsMetadata[];
    extensionSettings: ExtensionSettingsValues[];
    refreshModel: () => void;
    runbooks: RunbookResource[];
    octopusApprovalsFeatureEnabled: boolean;
    isTrial: boolean;
}) => {
    return (<>
            {props.extensionSettings?.map((extensionSettings) => {
            const metaData = props.metadata.filter((m) => m.ExtensionId === extensionSettings.ExtensionId)[0];
            if (metaData !== undefined) {
                const isSnowSettings = isServiceNowEnvironmentSettings(extensionSettings);
                if (isSnowSettings) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const serviceNowSettings = extensionSettings as ServiceNowEnvironmentSettings;
                    return <ServiceNowSettingsMenu serviceNowEnvironmentSettings={serviceNowSettings} refreshModel={props.refreshModel} metadata={metaData} runbooks={props.runbooks} isTrial={props.isTrial}/>;
                }
                const isJsmSettings = isJiraServiceManagementSettings(extensionSettings);
                if (isJsmSettings) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const JsmSettings = extensionSettings as JiraServiceManagementSettings;
                    return <JsmSettingsMenu JsmEnvironmentSettings={JsmSettings} refreshModel={props.refreshModel} metadata={metaData} runbooks={props.runbooks} isTrial={props.isTrial}/>;
                }
                const isApprovalsSettings = isOctopusApprovalsSettings(extensionSettings);
                if (isApprovalsSettings && props.octopusApprovalsFeatureEnabled) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const octopusApprovalsSettings = extensionSettings as OctopusApprovalsSettings;
                    return <OctopusApprovalsSettingsMenu octopusApprovalsSettings={octopusApprovalsSettings} refreshModel={props.refreshModel} metadata={metaData} runbooks={props.runbooks}/>;
                }
            }
        })}
        </>);
};
ExtensionSettings.displayName = "ExtensionSettings"
interface ServiceNowSettingsMenuProps {
    serviceNowEnvironmentSettings: ServiceNowEnvironmentSettings;
    refreshModel: () => void;
    metadata: ProjectSettingsMetadata;
    runbooks: RunbookResource[];
    isTrial: boolean;
}
const ServiceNowSettingsMenu: React.FunctionComponent<ServiceNowSettingsMenuProps> = (props: ServiceNowSettingsMenuProps) => {
    const snowMetadata = props.metadata.Metadata.Types.filter((t) => t.Name === "ServiceNow Integration")[0];
    const connectionIdOptions = snowMetadata.Properties.filter((p) => p.Name === "ServiceNowConnectionId")[0].DisplayInfo.Options.Values;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const record = connectionIdOptions as Record<string, string>;
    const serviceNowConnections = Object.keys(record).map((p) => ({ value: p, text: record[p] }));
    const automaticTransitionOptions = snowMetadata.Properties.filter((p) => p.Name === "AutomaticStateTransitionTarget")[0].DisplayInfo.Options.Values;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const automaticTransitionOptionsRecord = automaticTransitionOptions as Record<string, string>;
    const automaticTransitionItems = Object.keys(automaticTransitionOptionsRecord).map((p) => ({
        value: p,
        text: automaticTransitionOptionsRecord[p],
    }));
    const connectionId = props.serviceNowEnvironmentSettings.Values.ServiceNowConnectionId;
    const runbookSummary = props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds && props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds.length > 0
        ? `${props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds.length} ${pluralize("runbook", props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds.length)} change controlled`
        : "No runbooks are change controlled";
    return (<div key={props.serviceNowEnvironmentSettings.ExtensionId}>
            <FormSectionHeading title={<div className={styles.titleWrapper}>ServiceNow Integration {props.isTrial && <Badge variant="upsell" size="medium" label="Enterprise" hideIcon/>}</div>}/>

            <ExpandableFormSection title="Change Controlled" summary={Summary.summary(<span>{props.serviceNowEnvironmentSettings.Values.ServiceNowChangeControlled ? "Yes" : "No"}</span>)} help="If enabled, deployments from this project to change controlled environments will create or require a change request and wait for approval before deployments can continue." errorKey="Change Controlled">
                <Checkbox value={props.serviceNowEnvironmentSettings.Values.ServiceNowChangeControlled} label="Change Controlled" onChange={(val) => {
            props.serviceNowEnvironmentSettings.Values.ServiceNowChangeControlled = val;
            props.refreshModel();
        }}/>
                <Note>Define which environments are change controlled in each environment’s settings.</Note>
            </ExpandableFormSection>
            {props.serviceNowEnvironmentSettings.Values.ServiceNowChangeControlled && (<>
                    <ExpandableFormSection title="ServiceNow Connection" summary={Summary.summary(<span>{serviceNowConnections.find((i) => i.value === connectionId)?.text ?? connectionId}</span>)} help="Select the ServiceNow instance where Change Requests for this project will live." errorKey="ServiceNow Connection">
                        <DeprecatedSelect items={serviceNowConnections} allowClear={false} value={connectionId} label="ServiceNow Connection" onChange={(val) => {
                props.serviceNowEnvironmentSettings.Values.ServiceNowConnectionId = val ? val : "";
                props.refreshModel();
            }}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection title="Standard Change Template Name" summary={Summary.summary(<span>{props.serviceNowEnvironmentSettings.Values.StandardChangeTemplateName}</span>)} help="The name of a standard change template which exists in ServiceNow's Standard Change Catalogue" errorKey="Standard Change Template Name">
                        <DebounceText id={"standardChangeTemplateName"} label="Standard Change Template Name" value={props.serviceNowEnvironmentSettings.Values.StandardChangeTemplateName} onChange={(val: string) => {
                props.serviceNowEnvironmentSettings.Values.StandardChangeTemplateName = val ? val : "";
                props.refreshModel();
            }}/>
                        <Note>If provided, deployments will create a standard change based on the provided template, otherwise a normal change will be created.</Note>
                    </ExpandableFormSection>

                    <ExpandableFormSection title="Automatic Transition" summary={Summary.summary(<span>{props.serviceNowEnvironmentSettings.Values.AutomaticStateTransitionTarget}</span>)} help="Standard changes created by Octopus Deploy for this project will be automatically transitioned through to the desired state, a normal change will be created when selecting None" errorKey="Automatic Transition">
                        <DeprecatedSelect items={automaticTransitionItems} allowClear={false} value={props.serviceNowEnvironmentSettings.Values.AutomaticStateTransitionTarget} label="Automatic Transition" onChange={(val) => {
                props.serviceNowEnvironmentSettings.Values.AutomaticStateTransitionTarget = val ? val : "None";
                props.refreshModel();
            }}/>
                        <Note>
                            Automatic transition allows a continuous deployment (CD) workflow where a standard change is used to record the details of a deployment, after the deployment has completed the standard change will be moved to the desired
                            state
                        </Note>
                    </ExpandableFormSection>

                    {props.runbooks && props.runbooks.length > 0 && (<ExpandableFormSection key="snowrunbooks" title="Enabled Runbooks" summary={Summary.summary(runbookSummary)} help={"Select any runbooks that should be change controlled"} errorKey="snowrunbooks">
                            <RunbookMultiSelect items={props.runbooks} value={props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds} onChange={function (val: string[]): void {
                    props.serviceNowEnvironmentSettings.Values.ServiceNowRunbookIds = val;
                    props.refreshModel();
                }}/>
                        </ExpandableFormSection>)}
                </>)}
        </div>);
};
ServiceNowSettingsMenu.displayName = "ServiceNowSettingsMenu"
interface JsmSettingsMenuProps {
    JsmEnvironmentSettings: JiraServiceManagementSettings;
    refreshModel: () => void;
    metadata: ProjectSettingsMetadata;
    runbooks: RunbookResource[];
    isTrial: boolean;
}
const JsmSettingsMenu: React.FunctionComponent<JsmSettingsMenuProps> = (props: JsmSettingsMenuProps) => {
    const jsmMetadata = props.metadata.Metadata.Types.filter((t) => t.Name === "Jira Service Management Integration")[0];
    const connectionIdOptions = jsmMetadata.Properties.filter((p) => p.Name === "JsmConnectionId")[0].DisplayInfo.Options.Values;
    const connectionId = props.JsmEnvironmentSettings.Values.JsmConnectionId;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const record = connectionIdOptions as Record<string, string>;
    const JsmConnections = Object.keys(record).map((p) => ({ value: p, text: record[p] }));
    const runbookSummary = props.JsmEnvironmentSettings.Values.JsmRunbookIds && props.JsmEnvironmentSettings.Values.JsmRunbookIds.length > 0
        ? `${props.JsmEnvironmentSettings.Values.JsmRunbookIds.length} ${pluralize("runbook", props.JsmEnvironmentSettings.Values.JsmRunbookIds.length)} change controlled`
        : "No runbooks are change controlled";
    return (<div key={props.JsmEnvironmentSettings.ExtensionId}>
            <FormSectionHeading title={<div className={styles.titleWrapper}>Jira Service Management Integration {props.isTrial && <Badge variant="upsell" size="medium" label="Enterprise" hideIcon/>}</div>}/>

            <ExpandableFormSection title="Change Controlled" summary={Summary.summary(<span>{props.JsmEnvironmentSettings.Values.JsmChangeControlled ? "Yes" : "No"}</span>)} help="If enabled, deployments from this project to change controlled environments will create or require a change request and wait for approval before deployments can continue." errorKey="JSM Change Controlled">
                <Checkbox value={props.JsmEnvironmentSettings.Values.JsmChangeControlled} label="Change Controlled" onChange={(val) => {
            props.JsmEnvironmentSettings.Values.JsmChangeControlled = val;
            props.refreshModel();
        }}/>
                <Note>Define which environments are change controlled in each environment’s settings.</Note>
            </ExpandableFormSection>
            {props.JsmEnvironmentSettings.Values.JsmChangeControlled && (<>
                    <ExpandableFormSection title="Jira Service Management Connection" summary={Summary.summary(<span>{JsmConnections.find((i) => i.value === connectionId)?.text ?? connectionId}</span>)} help="Select the Jira Service Management instance where Change Requests for this project will live." errorKey="JSM Connection">
                        <DeprecatedSelect items={JsmConnections} allowClear={false} value={connectionId} onChange={(val) => {
                props.JsmEnvironmentSettings.Values.JsmConnectionId = val ? val : "";
                props.refreshModel();
            }} label={"Jira Service Management Connection"}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection title="Jira Service Management Service Desk Project" summary={Summary.summary(<span>{props.JsmEnvironmentSettings.Values.ServiceDeskProjectName}</span>)} help="Jira Service Management Service Desk Project" errorKey="Jira Service Management Service Desk Project">
                        <DebounceText id={"JsmServiceDeskProject"} value={props.JsmEnvironmentSettings.Values.ServiceDeskProjectName} onChange={(val: string) => {
                props.JsmEnvironmentSettings.Values.ServiceDeskProjectName = val ? val : "";
                props.refreshModel();
            }} label={"Jira Service Management Service Desk Project"}/>
                    </ExpandableFormSection>

                    {props.runbooks && props.runbooks.length > 0 && (<ExpandableFormSection key="jsmrunbooks" title="Enabled Runbooks" summary={Summary.summary(runbookSummary)} help={"Select any runbooks that should be change controlled"} errorKey="jsmrunbooks">
                            <RunbookMultiSelect items={props.runbooks} value={props.JsmEnvironmentSettings.Values.JsmRunbookIds} onChange={function (val: string[]): void {
                    props.JsmEnvironmentSettings.Values.JsmRunbookIds = val;
                    props.refreshModel();
                }}/>
                        </ExpandableFormSection>)}
                </>)}
        </div>);
};
JsmSettingsMenu.displayName = "JsmSettingsMenu"
interface OctopusApprovalsSettingsMenuProps {
    octopusApprovalsSettings: OctopusApprovalsSettings;
    refreshModel: () => void;
    metadata: ProjectSettingsMetadata;
    runbooks: RunbookResource[];
}
const OctopusApprovalsSettingsMenu: React.FunctionComponent<OctopusApprovalsSettingsMenuProps> = (props: OctopusApprovalsSettingsMenuProps) => {
    const octopusApprovalsMetadata = props.metadata.Metadata.Types.filter((t) => t.Name === "Octopus Approvals")[0];
    const runbookSummary = props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds && props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds.length > 0
        ? `${props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds.length} ${pluralize("runbook", props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds.length)} change controlled`
        : "No runbooks are change controlled";
    return (<div key={props.octopusApprovalsSettings.ExtensionId}>
            <FormSectionHeading title={"Octopus Approvals"}/>

            <ExpandableFormSection title="Change Controlled" summary={Summary.summary(<span>{props.octopusApprovalsSettings.Values.OctopusApprovalsChangeControlled ? "Yes" : "No"}</span>)} help="If enabled, deployments from this project to change controlled environments will create or require a change request and wait for approval before deployments can continue." errorKey="Octopus Approvals Change Controlled">
                <Checkbox value={props.octopusApprovalsSettings.Values.OctopusApprovalsChangeControlled} label="Change Controlled" onChange={(val) => {
            props.octopusApprovalsSettings.Values.OctopusApprovalsChangeControlled = val;
            props.refreshModel();
        }}/>
                <Note>Define which environments are change controlled in each environment's settings.</Note>
            </ExpandableFormSection>
            {props.octopusApprovalsSettings.Values.OctopusApprovalsChangeControlled && (<>
                    {props.runbooks && props.runbooks.length > 0 && (<ExpandableFormSection key="octopusapprovalsrunbooks" title="Enabled Runbooks" summary={Summary.summary(runbookSummary)} help={"Select any runbooks that should be change controlled"} errorKey="octopusapprovalsrunbooks">
                            <RunbookMultiSelect items={props.runbooks} value={props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds} onChange={function (val: string[]): void {
                    props.octopusApprovalsSettings.Values.OctopusApprovalsRunbookIds = val;
                    props.refreshModel();
                }}/>
                        </ExpandableFormSection>)}
                </>)}
        </div>);
};
OctopusApprovalsSettingsMenu.displayName = "OctopusApprovalsSettingsMenu"
const styles = {
    titleWrapper: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
    }),
};
